import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { TranslatedText } from '@ianesthesie-kit/core';
import { useAppSelector } from '@/hooks';
import { selectAppBarPatientIdentityShown } from '@/modules/ui';
import { useCurrentPatient } from '@/modules/surgicalEvent';
import { getInitials } from '@/util';

const enterEasing = 'cubic-bezier(0.16, 1, 0.3, 1)';
const exitEasing = 'cubic-bezier(0.7, 0, 0.84, 0)';

/**
 * The open patient's avatar, name and age. They rise into the app bar once the card's name line
 * has scrolled under it, and drop back out when it returns.
 */
export const AppBarPatientIdentity = () => {
  const shown = useAppSelector(selectAppBarPatientIdentityShown);
  const { currentData: patient } = useCurrentPatient();

  return (
    <Box flex={1} minWidth={0} pl={1} display={{ xs: 'none', md: 'block' }}>
      {!!patient && (
        <Box
          data-sentry-mask
          aria-hidden={!shown}
          display="flex"
          alignItems="center"
          gap={1.25}
          sx={{
            opacity: shown ? 1 : 0,
            visibility: shown ? 'visible' : 'hidden',
            transform: shown ? 'none' : 'translateY(28px)',
            // Exits faster than it enters; visibility flips only once the exit is done
            transition: shown
              ? `transform 280ms ${enterEasing}, opacity 200ms linear, visibility 0s`
              : `transform 140ms ${exitEasing}, opacity 120ms linear, visibility 0s 140ms`,
            '@media (prefers-reduced-motion: reduce)': { transform: 'none' }
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'themeColors.indigo.50',
              color: 'themeColors.slate.700',
              width: 32,
              height: 32,
              fontSize: 13,
              fontWeight: 600
            }}
            imgProps={{ crossOrigin: 'anonymous' }}
            src={patient.facePicture?.path}
          >
            {getInitials(patient)}
          </Avatar>
          {/* Name and age differ in size, so they line up on their text baseline */}
          <Box display="flex" alignItems="baseline" gap={1.25} minWidth={0}>
            <Typography
              noWrap
              fontSize={17}
              fontWeight={600}
              title={patient.name}
            >
              {patient.name}
            </Typography>
            <Typography
              noWrap
              flexShrink={0}
              fontSize={15}
              fontWeight={500}
              color="text.secondary"
            >
              {patient.age}&nbsp;
              <TranslatedText id="doctor-dashboard.yearsOld.abbr" />
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
